
import { FSharpRef, Record } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { tuple_type, record_type, list_type, bool_type, string_type, int32_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { getLatestCanvas, LoadStatus, copyFile, removeFileWithExtn, tryLoadComponentFromPath, tryLoadComponentFromText, baseNameWithoutExtension, hasExtn, unlink, readFilesFromDirectoryWithExtn, staticFileDirectory, tryUserDataDirectory, tryEnsureDirectory, readFilesFromDirectory, isDirectory, exists, writeFile, baseName, tryReadFileSync, pathJoin } from "./FilesIO.js";
import { Result_MapError, Result_Bind, Result_Map, FSharpResult$2 } from "../fable_modules/fable-library-js.5.13.0/Result.js";
import { Convert_fromJson } from "../Common/SimpleJson/Json.Converter.js";
import { SimpleJson_parseNative } from "../Common/SimpleJson/SimpleJson.js";
import { createTypeInfo } from "../Common/SimpleJson/TypeInfo.Converter.js";
import { substring, split, concat } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { Fable_SimpleJson_Json__Json_stringify_Static_4E60E31B } from "../Common/SimpleJson/Json.Converter.js";
import { collect, tryLast, cons, tryFind, contains as contains_1, length, iterate, isEmpty, singleton, exists as exists_1, choose, append, sortBy, map, empty, filter } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { contains, ofList } from "../fable_modules/fable-library-js.5.13.0/Set.js";
import { stringHash, comparePrimitives } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { ResultList_traverse, JsonHelpers_jsonStringToState, RegenerateIds_regenerateSheetIds, JsonHelpers_stateToJsonStringAt, JsonHelpers_stateToJsonString, ResultList_sequence, ResultList_fold } from "../Common/Helpers.js";
import { isWithin } from "./PathHelpers.js";
import { List_distinct } from "../fable_modules/fable-library-js.5.13.0/Seq2.js";
import { defaultArg } from "../fable_modules/fable-library-js.5.13.0/Option.js";
import { isClocked, LoadedComponent, SheetInfo, CCForm } from "../Common/CommonTypes.js";
import { warn, error } from "../Shared/Log.js";
import { isDev } from "./Bridge.js";
import { parseDiagramSignature } from "../Simulator/CanvasExtractor.js";
import { tryHead } from "../fable_modules/fable-library-js.5.13.0/Array.js";
import { tryParse } from "../fable_modules/fable-library-js.5.13.0/Int32.js";
import { initializeInfinite, find } from "../fable_modules/fable-library-js.5.13.0/Seq.js";

export const Constants_librariesDirectory = "libraries";

export const Constants_componentExtension = ".ldgm";

export const Constants_defaultSection = "Components";

export const Constants_currentFormatVersion = 1;

/**
 * What the catalogue needs to know about a component without reading its sheet.
 * 
 * Deliberately does NOT carry the component's parameters, ports, or anything else derived from
 * the sheet. All of that is read from the body, once, just before it is needed - which is why
 * this cannot drift from the sheet it describes.
 */
export class LibraryHeader extends Record {
    constructor(FormatVersion, Name, Description, Section, OfferedInCatalogue, Requires) {
        super();
        this.FormatVersion = (FormatVersion | 0);
        this.Name = Name;
        this.Description = Description;
        this.Section = Section;
        this.OfferedInCatalogue = OfferedInCatalogue;
        this.Requires = Requires;
    }
}

export function LibraryHeader_$reflection() {
    return record_type("ComponentLibraries.LibraryHeader", [], LibraryHeader, () => [["FormatVersion", int32_type], ["Name", string_type], ["Description", string_type], ["Section", string_type], ["OfferedInCatalogue", bool_type], ["Requires", list_type(string_type)]]);
}

/**
 * A library as offered in the catalogue: a name and a directory. Its components are read when the
 * user opens it, never at startup.
 */
export class ComponentLibrary extends Record {
    constructor(Name, Path) {
        super();
        this.Name = Name;
        this.Path = Path;
    }
}

export function ComponentLibrary_$reflection() {
    return record_type("ComponentLibraries.ComponentLibrary", [], ComponentLibrary, () => [["Name", string_type], ["Path", string_type]]);
}

/**
 * One component of an opened library, as listed.
 */
export class LibraryListing extends Record {
    constructor(Header, Path) {
        super();
        this.Header = Header;
        this.Path = Path;
    }
}

export function LibraryListing_$reflection() {
    return record_type("ComponentLibraries.LibraryListing", [], LibraryListing, () => [["Header", LibraryHeader_$reflection()], ["Path", string_type]]);
}

/**
 * What opening a library found: the components it can offer, and anything that would not read.
 * A file that will not read costs that component, never the library.
 */
export class OpenedLibrary extends Record {
    constructor(Name, Path, Components, Problems) {
        super();
        this.Name = Name;
        this.Path = Path;
        this.Components = Components;
        this.Problems = Problems;
    }
}

export function OpenedLibrary_$reflection() {
    return record_type("ComponentLibraries.OpenedLibrary", [], OpenedLibrary, () => [["Name", string_type], ["Path", string_type], ["Components", list_type(LibraryListing_$reflection())], ["Problems", list_type(string_type)]]);
}

export function componentPath(libPath, name) {
    return pathJoin([libPath, name + Constants_componentExtension]);
}

/**
 * Read one .ldgm. The body comes back as text: there is little to gain from decoding less, since
 * it is a single JSON string token either way.
 */
export function tryReadComponentFile(path) {
    const matchValue = tryReadFileSync(path);
    if (matchValue.tag === 0) {
        let parsed;
        try {
            parsed = (new FSharpResult$2(/* Ok */ 0, [Convert_fromJson(SimpleJson_parseNative(matchValue.fields[0]), createTypeInfo(tuple_type(LibraryHeader_$reflection(), string_type)))]));
        }
        catch (ex) {
            parsed = (new FSharpResult$2(/* Error */ 1, [ex.message]));
        }
        if (parsed.tag === 0) {
            const header = parsed.fields[0][0];
            if (header.FormatVersion > Constants_currentFormatVersion) {
                return new FSharpResult$2(/* Error */ 1, [`${baseName(path)} was written by a later version of Issie: it uses library format ${header.FormatVersion} and this version reads ${Constants_currentFormatVersion}`]);
            }
            else {
                return new FSharpResult$2(/* Ok */ 0, [[header, parsed.fields[0][1]]]);
            }
        }
        else {
            return new FSharpResult$2(/* Error */ 1, [concat(baseName(path), " is not a readable library component (", parsed.fields[0], ")")]);
        }
    }
    else {
        return new FSharpResult$2(/* Error */ 1, [matchValue.fields[0]]);
    }
}

/**
 * The header of one component.
 */
export function tryReadHeader(path) {
    return Result_Map((tuple) => tuple[0], tryReadComponentFile(path));
}

/**
 * Write a component file to a path chosen by the caller. `body` must be the text of a .dgm
 * exactly as the sheet was saved, since that is what is written back out when it is used.
 */
export function writeComponentFileAt(path, header, body) {
    return writeFile(path, Fable_SimpleJson_Json__Json_stringify_Static_4E60E31B([header, body]));
}

/**
 * Write a component into a library, under the name its header carries. What "save as library
 * component" uses; a library opened as a project writes to the file each sheet came from, which
 * is writeComponentFileAt above.
 */
export function writeComponentFile(libPath, header, body) {
    return writeComponentFileAt(componentPath(libPath, header.Name), header, body);
}

function subdirectoriesOf(path) {
    const matchValue = exists(path);
    if (matchValue) {
        return filter((name) => isDirectory(pathJoin([path, name])), readFilesFromDirectory(path));
    }
    else {
        return empty();
    }
}

/**
 * Where libraries the user makes or imports are kept. Those shipped with Issie stay read-only
 * under the installation; this is the writable side. Error when the directory cannot be made -
 * see FilesIO.tryUserDataDirectory for why that is a real possibility and not a theoretical one.
 */
export function tryUserLibrariesDirectory() {
    return Result_Bind((root) => tryEnsureDirectory(pathJoin([root, Constants_librariesDirectory])), tryUserDataDirectory());
}

/**
 * The libraries available: those shipped with Issie, and those the user has made or imported.
 * Directory names only - no file is opened - so this is cheap enough for startup, which it has to
 * be: the catalogue is a pure render function and cannot read the disk itself. Everything about a
 * component is read later, when its library is opened.
 * A user library with the same name as a shipped one wins, so a library can be overridden.
 */
export function findLibraries() {
    const librariesIn = (root) => map((name) => (new ComponentLibrary(name, pathJoin([root, name]))), subdirectoriesOf(root));
    const shipped = librariesIn(pathJoin([staticFileDirectory, Constants_librariesDirectory]));
    let user;
    const matchValue = tryUserLibrariesDirectory();
    user = ((matchValue.tag === 1) ? empty() : librariesIn(matchValue.fields[0]));
    const userNames = ofList(map((lib) => lib.Name, user), {
        Compare: (x, y) => (comparePrimitives(x, y) | 0),
    });
    return sortBy((lib_2) => lib_2.Name, append(user, filter((lib_1) => !contains(lib_1.Name, userNames), shipped)), {
        Compare: (x_1, y_1) => (comparePrimitives(x_1, y_1) | 0),
    });
}

/**
 * Read the headers of a library's components. Done when the user opens the library, and not kept:
 * it is one small read per component, on an action the user took, and keeping it would mean
 * deciding when it had gone wrong.
 */
export function openLibrary(library) {
    const read = map((fileName) => {
        const path = pathJoin([library.Path, fileName]);
        return [path, tryReadHeader(path)];
    }, readFilesFromDirectoryWithExtn(library.Path, Constants_componentExtension));
    return new OpenedLibrary(library.Name, library.Path, sortBy((listing) => listing.Header.Name, choose((tupledArg) => {
        const header = tupledArg[1];
        let matchResult;
        if (header.tag === 0) {
            if (header.fields[0].OfferedInCatalogue) {
                matchResult = 0;
            }
            else {
                matchResult = 1;
            }
        }
        else {
            matchResult = 1;
        }
        switch (matchResult) {
            case 0:
                return new LibraryListing(header.fields[0], tupledArg[0]);
            default:
                return undefined;
        }
    }, read), {
        Compare: (x, y) => (comparePrimitives(x, y) | 0),
    }), choose((tupledArg_1) => {
        const header_3 = tupledArg_1[1];
        if (header_3.tag === 0) {
            return undefined;
        }
        else {
            return header_3.fields[0];
        }
    }, read));
}

/**
 * A component and everything it needs, dependencies first - the order they must be written in.
 * Dependencies are named rather than embedded, so they are read from the same library here. A
 * name that is not there is an error rather than a silent omission: the component would otherwise
 * be placed holding a custom component that refers to a sheet which does not exist.
 */
export function readComponentAndDependencies(libPath, name) {
    const read = (got, name_1) => {
        if (exists_1((tupledArg) => (tupledArg[0].Name === name_1), got)) {
            return new FSharpResult$2(/* Ok */ 0, [got]);
        }
        else {
            return Result_Bind((tupledArg_1) => {
                const header_1 = tupledArg_1[0];
                return Result_Map((got_1) => append(got_1, singleton([header_1, tupledArg_1[1]])), ResultList_fold(read, got, header_1.Requires));
            }, tryReadComponentFile(componentPath(libPath, name_1)));
        }
    };
    return read(empty(), name);
}

/**
 * What exporting a library did: how many components were written, and how many were removed from
 * the destination because this library no longer has them.
 */
export class ExportResult extends Record {
    constructor(Written, Removed, Destination) {
        super();
        this.Written = (Written | 0);
        this.Removed = (Removed | 0);
        this.Destination = Destination;
    }
}

export function ExportResult_$reflection() {
    return record_type("ComponentLibraries.ExportResult", [], ExportResult, () => [["Written", int32_type], ["Removed", int32_type], ["Destination", string_type]]);
}

/**
 * Copy a library into `destRoot`, in a subdirectory named after the library - created if it is not
 * there, brought up to date if it is.
 * 
 * What lands is a LIBRARY and not a heap of files: the destination ends up holding exactly the
 * components this one holds. A component renamed or deleted since a previous export would
 * otherwise stay behind in the copy and go on being offered from it, which is a library that
 * exists nowhere. Only .ldgm files are touched, and only inside the subdirectory named after the
 * library, so nothing else in the folder the user chose is at risk.
 * 
 * The files are copied verbatim rather than read and written back. An .ldgm is a header and the
 * exact text of a .dgm; an export that re-encoded them could differ from its source for reasons
 * that have nothing to do with the components in it.
 * 
 * Names are compared case-insensitively when deciding what is stale. On Windows "Adder.ldgm" and
 * "adder.ldgm" ARE the same file, so an exact comparison would delete the one just written; on
 * systems where they differ this errs towards leaving a file alone, which is the safe direction.
 */
export function exportLibraryTo(destRoot, library) {
    const dest = pathJoin([destRoot, library.Name]);
    const componentFiles = readFilesFromDirectoryWithExtn(library.Path, Constants_componentExtension);
    if (isEmpty(componentFiles)) {
        return new FSharpResult$2(/* Error */ 1, [concat(library.Name, " holds no components to export")]);
    }
    else if (isWithin(dest, library.Path) ? true : isWithin(library.Path, dest)) {
        return new FSharpResult$2(/* Error */ 1, [concat(library.Name, " already lives there - choose a folder outside ", library.Path)]);
    }
    else {
        const componentFiles_1 = componentFiles;
        return Result_Bind((dest_1) => Result_Map((_arg) => {
            const exported = ofList(map((name_1) => name_1.toLowerCase(), componentFiles_1), {
                Compare: (x, y) => (comparePrimitives(x, y) | 0),
            });
            const stale = filter((name_2) => !contains(name_2.toLowerCase(), exported), readFilesFromDirectoryWithExtn(dest_1, Constants_componentExtension));
            iterate((name_3) => {
                unlink(pathJoin([dest_1, name_3]));
            }, stale);
            return new ExportResult(length(componentFiles_1), length(stale), dest_1);
        }, ResultList_sequence(map((name) => {
            let result, path;
            return Result_MapError((msg) => concat(name, " could not be exported: ", msg), (result = tryReadFileSync(pathJoin([library.Path, name])), Result_Bind((path = pathJoin([dest_1, name]), (data) => writeFile(path, data)), result)));
        }, componentFiles_1))), tryEnsureDirectory(dest));
    }
}

/**
 * Whether a sheet's file is a library component rather than an ordinary .dgm.
 */
export function isLibraryComponentFile(filePath) {
    return hasExtn(Constants_componentExtension, filePath);
}

/**
 * Whether this project IS a library, opened to be edited in place.
 * 
 * Read off the sheets' own files rather than carried in the model: the two forms differ only in
 * how each sheet is stored, so the files are where the difference actually is.
 */
export function isLibraryProject(project) {
    return exists_1((ldc) => isLibraryComponentFile(ldc.FilePath), project.LoadedComponents);
}

/**
 * The extension a project's sheets are stored with. A project is all of one form or all of the
 * other, so this is a fact about the project and not about each sheet.
 */
export function sheetExtension(project) {
    if (isLibraryProject(project)) {
        return Constants_componentExtension;
    }
    else {
        return ".dgm";
    }
}

/**
 * The file one sheet of a project is kept in - including a sheet that is about to be added, which
 * takes the form its siblings are in rather than becoming a stray .dgm the library loader would
 * never read.
 */
export function sheetFilePath(project, sheetName) {
    return pathJoin([project.ProjectPath, sheetName + sheetExtension(project)]);
}

function customSheetsOnCanvas(_arg1_, _arg1__1) {
    return List_distinct(choose((comp) => {
        const matchValue = comp.Type;
        if (matchValue.tag === 26) {
            return matchValue.fields[0].Name;
        }
        else {
            return undefined;
        }
    }, _arg1_), {
        Equals: (x, y) => (x === y),
        GetHashCode: (x) => (stringHash(x) | 0),
    });
}

function headerForSheet(path, description, canvas_, canvas__1) {
    const canvas = [canvas_, canvas__1];
    const existing = tryReadHeader(path);
    const orExisting = (pick, fallback) => {
        if (existing.tag === 1) {
            return fallback;
        }
        else {
            return pick(existing.fields[0]);
        }
    };
    const name = baseNameWithoutExtension(path);
    return new LibraryHeader(Constants_currentFormatVersion, name, defaultArg(description, orExisting((h) => h.Description, name)), orExisting((h_1) => h_1.Section, Constants_defaultSection), orExisting((h_2) => h_2.OfferedInCatalogue, false), customSheetsOnCanvas(canvas[0], canvas[1]));
}

function putSheetInFile(filePath, _arg1_, _arg1__1, _arg1__2, json) {
    let option_1;
    const _arg = [_arg1_, _arg1__1, _arg1__2];
    const canvas = _arg[0];
    const matchValue = isLibraryComponentFile(filePath);
    if (matchValue) {
        return writeComponentFileAt(filePath, headerForSheet(filePath, (option_1 = _arg[2], (option_1 != null) ? option_1.Description : undefined), canvas[0], canvas[1]), json);
    }
    else {
        return writeFile(filePath, json);
    }
}

/**
 * Write one sheet to its own file. The single funnel every sheet save goes through, so that
 * "which kind of project is this" is asked once and in one place.
 */
export function writeSheetFile(filePath, state_, state__1, state__2) {
    const state = [state_, state__1, state__2];
    return Result_Bind((json) => putSheetInFile(filePath, state[0], state[1], state[2], json), JsonHelpers_stateToJsonString(state[0], state[1], state[2]));
}

/**
 * As writeSheetFile, keeping a timestamp the caller already has rather than stamping the moment
 * of writing. For a rewrite the user did not ask for - the id conversion done on load - where the
 * stamp is what says which sheet they were last working on.
 */
export function writeSheetFileAt(timeStamp, filePath, state_, state__1, state__2) {
    const state = [state_, state__1, state__2];
    return Result_Bind((json) => putSheetInFile(filePath, state[0], state[1], state[2], json), JsonHelpers_stateToJsonStringAt(timeStamp, state[0], state[1], state[2]));
}

/**
 * The text of the .dgm a sheet's file holds, unwrapped from its header where it has one. What
 * "save as library component" copies: an .ldgm's body IS a .dgm, so a component can be written
 * into another library from a library opened as a project as readily as from an ordinary sheet.
 */
export function trySheetFileBody(filePath) {
    const matchValue = isLibraryComponentFile(filePath);
    if (matchValue) {
        return Result_Map((tuple) => tuple[1], tryReadComponentFile(filePath));
    }
    else {
        return tryReadFileSync(filePath);
    }
}

/**
 * Read a sheet from its file, in whichever of the two forms that file is.
 */
export function tryLoadSheetFile(filePath) {
    const matchValue = isLibraryComponentFile(filePath);
    if (matchValue) {
        return Result_Bind((tupledArg) => tryLoadComponentFromText(filePath, tupledArg[1]), tryReadComponentFile(filePath));
    }
    else {
        return tryLoadComponentFromPath(filePath);
    }
}

/**
 * Create the file for a sheet a project does not have yet, empty.
 */
export function createEmptySheetFile(project, name) {
    return writeSheetFile(sheetFilePath(project, name), [empty(), empty()], undefined, new SheetInfo(CCForm.User, undefined, undefined, undefined, undefined));
}

/**
 * Write every sheet of a project to disk. Used where a change reaches sheets other than the open
 * one - a custom component's ports changing shape, and a project being renamed.
 */
export function writeAllSheetFiles(project) {
    iterate((ldc) => {
        const sheetInfo = new SheetInfo(ldc.Form, ldc.Description, ldc.LCParameterSlots, ldc.IsTopSheet, ldc.ArrayInfo);
        writeSheetFile(sheetFilePath(project, ldc.Name), ldc.CanvasState, ldc.WaveInfo, sheetInfo);
        removeFileWithExtn(".dgmauto", project.ProjectPath, ldc.Name);
    }, project.LoadedComponents);
}

/**
 * Copy a sheet from some source path to a destination path, giving every component, port and
 * connection in it a fresh id so that it cannot clash with the sheet it was copied from. Either
 * path may be a library component or an ordinary sheet, so this is also how a component is copied
 * out of a library into a project and back.
 * Falls back to a plain file copy if the source cannot be read as a sheet.
 */
export function copySheetWithNewIds(sourcePath, newPath) {
    const matchValue = tryLoadSheetFile(sourcePath);
    if (matchValue.tag === 0) {
        const ldc$0027 = RegenerateIds_regenerateSheetIds(matchValue.fields[0]);
        const matchValue_1 = writeSheetFile(newPath, ldc$0027.CanvasState, ldc$0027.WaveInfo, new SheetInfo(ldc$0027.Form, ldc$0027.Description, ldc$0027.LCParameterSlots, undefined, ldc$0027.ArrayInfo));
        if (matchValue_1.tag === 1) {
            error(matchValue_1.fields[0]);
        }
    }
    else {
        warn(matchValue.fields[0]);
        copyFile(sourcePath, newPath);
    }
}

/**
 * Load a library directory as a project: every component in it becomes a sheet, and the file it
 * came from is where it is saved back to.
 * 
 * The sheets come back as User whatever the .ldgm says. A library sheet materialised INTO a
 * project is marked Library so that it is hidden and held read-only - it is one thing the user
 * placed, not a sheet of their own design. Here the sheets ARE the design, and marking them that
 * way would open the library into an editor that refuses to edit it.
 * 
 * Nor is any of them the top: which component of a library is "the" design is not a question a
 * library answers, and a flag left over from the project a component was authored in would make
 * one of them the answer at random.
 */
export function tryLoadLibraryProject(libPath) {
    const matchValue = readFilesFromDirectoryWithExtn(libPath, Constants_componentExtension);
    if (isEmpty(matchValue)) {
        return new FSharpResult$2(/* Error */ 1, [concat(baseName(libPath), " holds no library components")]);
    }
    else {
        return ResultList_sequence(map((fileName) => {
            const path = pathJoin([libPath, fileName]);
            return Result_Map((tupledArg_1) => {
                const header_1 = tupledArg_1[0];
                const ldc_1 = tupledArg_1[1];
                return new LoadStatus(/* OkComp */ 1, [new LoadedComponent(header_1.Name, ldc_1.TimeStamp, ldc_1.FilePath, ldc_1.WaveInfo, ldc_1.CanvasState, ldc_1.InputLabels, ldc_1.OutputLabels, ldc_1.LCParameterSlots, CCForm.User, ldc_1.LoadedComponentIsOutOfDate, header_1.Description, false, ldc_1.ArrayInfo)]);
            }, Result_Bind((tupledArg) => Result_Map((ldc) => [tupledArg[0], ldc], tryLoadComponentFromText(path, tupledArg[1])), tryReadComponentFile(path)));
        }, matchValue));
    }
}

/**
 * Where the libraries shipped with Issie live: inside the installation, and read-only for anyone
 * who installed it.
 */
export function shippedLibrariesDirectory() {
    return pathJoin([staticFileDirectory, Constants_librariesDirectory]);
}

/**
 * Whether a library is one Issie manages rather than one the user wrote.
 * 
 * Two directories are Issie's own. The shipped libraries under the installation are what comes
 * with the program. The user library directory is the store: it is where a library ARRIVES - the
 * copy made when a component is saved into a library from a sheet, and the copy an import will
 * leave - and a copy is not the thing to edit. A library the user is actually working on lives
 * wherever they keep their work, the way a project does.
 */
export function isManagedLibrary(libPath) {
    const within = (root) => {
        if (root.tag === 1) {
            return false;
        }
        else {
            return isWithin(root.fields[0], libPath);
        }
    };
    if (within(new FSharpResult$2(/* Ok */ 0, [shippedLibrariesDirectory()]))) {
        return true;
    }
    else {
        return within(tryUserLibrariesDirectory());
    }
}

/**
 * Whether the library in this directory may be opened as a project and saved again.
 * 
 * Not the standard libraries, and not the imported ones: what is in Issie's own directories
 * arrived from somewhere else, and editing a copy in the form it arrived in produces a version
 * that agrees with nothing. A library kept anywhere else is the user's own work and is theirs to
 * change - including one somebody handed them, which they have to put somewhere first.
 * 
 * A development run may edit anything, the shipped libraries above all: that is where they are
 * maintained, and the checkout is writable there (see Main/Bridge.fs).
 * 
 * Nothing here is prominent, and that is deliberate. Writing a library component is
 * sheet -> save into a library -> place it and try it -> change it, and the last step must not
 * need a second, non-library copy of the sheet kept in step by hand. But it is a thing to go and
 * do rather than a thing to fall into: the catalogue only offers it where it applies, and
 * otherwise a library is opened by navigating to the folder it is in.
 */
export function libraryPathIsEditable(libPath) {
    if (isDev) {
        return true;
    }
    else {
        return !isManagedLibrary(libPath);
    }
}

export function libraryIsEditable(library) {
    return libraryPathIsEditable(library.Path);
}

/**
 * What a library component becomes on the canvas, without materialising it: the ports its symbol
 * will have, and whether that symbol is drawn as clocked.
 * 
 * This is what the catalogue needs to draw a component being carried to the sheet. It is read
 * from the sheet rather than declared in the header for the reason the header gives: nothing
 * derived is stored, so nothing can be out of date with the sheet it describes.
 */
export class ComponentShape extends Record {
    constructor(InputLabels, OutputLabels, IsClocked) {
        super();
        this.InputLabels = InputLabels;
        this.OutputLabels = OutputLabels;
        this.IsClocked = IsClocked;
    }
}

export function ComponentShape_$reflection() {
    return record_type("ComponentLibraries.ComponentShape", [], ComponentShape, () => [["InputLabels", list_type(tuple_type(string_type, int32_type))], ["OutputLabels", list_type(tuple_type(string_type, int32_type))], ["IsClocked", bool_type]]);
}

function tryCanvasOfBody(name, body) {
    const matchValue = JsonHelpers_jsonStringToState(body);
    if (matchValue.tag === 0) {
        return new FSharpResult$2(/* Ok */ 0, [getLatestCanvas(matchValue.fields[0])]);
    }
    else {
        return new FSharpResult$2(/* Error */ 1, [concat(name, " does not hold a readable sheet (", matchValue.fields[0], ")")]);
    }
}

function canvasIsClocked(sheets, visited, _arg1_, _arg1__1) {
    return exists_1((comp) => {
        let option_1, canvas;
        const matchValue = comp.Type;
        if (matchValue.tag === 26) {
            if (!contains_1(matchValue.fields[0].Name, visited, {
                Equals: (x, y) => (x === y),
                GetHashCode: (x) => (stringHash(x) | 0),
            })) {
                return defaultArg((option_1 = tryFind((tupledArg) => (tupledArg[0] === matchValue.fields[0].Name), sheets), (option_1 != null) ? ((canvas = option_1[1], canvasIsClocked(sheets, cons(matchValue.fields[0].Name, visited), canvas[0], canvas[1]))) : undefined), false);
            }
            else {
                return false;
            }
        }
        else {
            return isClocked(empty(), empty(), comp);
        }
    }, _arg1_);
}

/**
 * The shape of one component of a library, read from the same files placing it will read.
 * 
 * Every sheet is decoded, not just the component's own: whether it is clocked can depend on a
 * sheet it uses, and its ports come from the last one, which is the component itself.
 */
export function tryReadComponentShape(libPath, name) {
    return Result_Bind((sheets) => {
        const matchValue = tryLast(sheets);
        if (matchValue != null) {
            const canvas_1 = matchValue[1];
            const patternInput = parseDiagramSignature(canvas_1[0], canvas_1[1]);
            return new FSharpResult$2(/* Ok */ 0, [new ComponentShape(patternInput[0], patternInput[1], canvasIsClocked(sheets, empty(), canvas_1[0], canvas_1[1]))]);
        }
        else {
            return new FSharpResult$2(/* Error */ 1, [concat("Library component ", name, " has no sheet")]);
        }
    }, Result_Bind((items) => ResultList_traverse((_arg) => {
        const header = _arg[0];
        return Result_Map((canvas) => [header.Name, canvas], tryCanvasOfBody(header.Name, _arg[1]));
    }, items), readComponentAndDependencies(libPath, name)));
}

/**
 * The sheets a sheet instantiates, by name and without duplicates. Used to fill in a header's
 * Requires when a component is saved, and to find everything that must be saved with it.
 */
export function customSheetsUsedBy(ldc) {
    return List_distinct(choose((comp) => {
        const matchValue = comp.Type;
        if (matchValue.tag === 26) {
            return matchValue.fields[0].Name;
        }
        else {
            return undefined;
        }
    }, ldc.CanvasState[0]), {
        Equals: (x, y) => (x === y),
        GetHashCode: (x) => (stringHash(x) | 0),
    });
}

/**
 * The name a library component takes as a sheet of a project.
 */
export function sheetNameFor(libraryIndex, compName) {
    return `L${libraryIndex}_${compName}`;
}

/**
 * The library and index a sheet belongs to, if it is a library sheet.
 */
export function libraryOfSheet(ldc) {
    let prefix;
    const matchValue = ldc.Form;
    let matchResult, libName;
    if (matchValue != null) {
        if (matchValue.tag === 1) {
            matchResult = 0;
            libName = matchValue.fields[0];
        }
        else {
            matchResult = 1;
        }
    }
    else {
        matchResult = 1;
    }
    switch (matchResult) {
        case 0: {
            const matchValue_1 = tryHead(split(ldc.Name, ["_"], undefined, 0));
            let matchResult_1, prefix_1;
            if (matchValue_1 != null) {
                if ((prefix = matchValue_1, (prefix.length > 1) && prefix.startsWith("L"))) {
                    matchResult_1 = 0;
                    prefix_1 = matchValue_1;
                }
                else {
                    matchResult_1 = 1;
                }
            }
            else {
                matchResult_1 = 1;
            }
            switch (matchResult_1) {
                case 0: {
                    let matchValue_2;
                    let outArg = 0;
                    matchValue_2 = [tryParse(substring(prefix_1, 1), 511, false, 32, new FSharpRef(() => (outArg | 0), (v) => {
                        outArg = (v | 0);
                    })), outArg];
                    if (matchValue_2[0]) {
                        return [libName, matchValue_2[1]];
                    }
                    else {
                        return undefined;
                    }
                }
                default:
                    return undefined;
            }
        }
        default:
            return undefined;
    }
}

/**
 * The prefix owned by a library index.
 */
export function prefixFor(libraryIndex) {
    return `L${libraryIndex}_`;
}

/**
 * The index to use for a library in this project: the one it already has if any of its sheets
 * are present, otherwise the lowest free index.
 * An index is free when no other library holds it and NO existing sheet begins with its prefix -
 * not merely none of the names this library would produce. User sheet names may themselves
 * contain underscores, so a project can already hold a sheet called L1_Anything, and letting a
 * library share a prefix with an unrelated sheet would be confusing even where nothing actually
 * collides. A prefix in use is skipped rather than anything being renamed or refused.
 */
export function libraryIndexFor(ldcs, libraryName) {
    const existing = choose(libraryOfSheet, ldcs);
    const matchValue = tryFind((tupledArg) => (tupledArg[0] === libraryName), existing);
    if (matchValue == null) {
        const takenIndices = ofList(map((tuple) => (tuple[1] | 0), existing), {
            Compare: (x, y) => (comparePrimitives(x, y) | 0),
        });
        const names = map((ldc_1) => ldc_1.Name, ldcs);
        return find((n_2) => {
            if (!contains(n_2, takenIndices)) {
                return !exists_1((name) => name.startsWith(prefixFor(n_2)), names);
            }
            else {
                return false;
            }
        }, initializeInfinite((i) => ((i + 1) | 0))) | 0;
    }
    else {
        return matchValue[1] | 0;
    }
}

/**
 * The prefixes the libraries used by this project own. Once a library holds a prefix no sheet may
 * be named into it, or a component of that library added later would have nowhere to go.
 */
export function reservedPrefixes(ldcs) {
    return List_distinct(map((arg) => prefixFor(arg[1]), choose(libraryOfSheet, ldcs)), {
        Equals: (x, y) => (x === y),
        GetHashCode: (x) => (stringHash(x) | 0),
    });
}

/**
 * The reserved prefix a proposed sheet name would intrude on, if any.
 * Used to refuse the name when a sheet is created or renamed.
 */
export function reservedPrefixOf(ldcs, sheetName) {
    return tryFind((prefix) => sheetName.startsWith(prefix), reservedPrefixes(ldcs));
}

/**
 * True when a sheet came from a library.
 */
export function isLibrarySheet(ldc) {
    const matchValue = ldc.Form;
    let matchResult;
    if (matchValue != null) {
        if (matchValue.tag === 1) {
            matchResult = 0;
        }
        else {
            matchResult = 1;
        }
    }
    else {
        matchResult = 1;
    }
    switch (matchResult) {
        case 0:
            return true;
        default:
            return false;
    }
}

/**
 * Library sheets no sheet instantiates any more, and so which should be dropped from the project.
 * A library sheet used only by another library sheet of the same component is kept, since that
 * one is reachable; the calculation is repeated until it settles so that a multi-sheet component
 * goes in one piece.
 * Deliberately NOT run when the instance is deleted: undo restores model snapshots, so deleting
 * the sheet there would leave undo unable to bring it back. Sweeping when the project is saved
 * or closed keeps deletion undoable.
 */
export function unusedLibrarySheets(ldcs) {
    const instantiated = ofList(collect((ldc) => choose((comp) => {
        const matchValue = comp.Type;
        if (matchValue.tag === 26) {
            return matchValue.fields[0].Name;
        }
        else {
            return undefined;
        }
    }, ldc.CanvasState[0]), ldcs), {
        Compare: (x, y) => (comparePrimitives(x, y) | 0),
    });
    const unused = filter((ldc_1) => {
        if (isLibrarySheet(ldc_1)) {
            return !contains(ldc_1.Name, instantiated);
        }
        else {
            return false;
        }
    }, ldcs);
    if (isEmpty(unused)) {
        return empty();
    }
    else {
        const unusedNames = ofList(map((ldc_2) => ldc_2.Name, unused), {
            Compare: (x_1, y_1) => (comparePrimitives(x_1, y_1) | 0),
        });
        return append(unused, unusedLibrarySheets(filter((ldc_3) => !contains(ldc_3.Name, unusedNames), ldcs)));
    }
}

